import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { ApiResponse } from "../api";
import type { PublicEventResponse } from "./useEventPublic";
import { useEventStateContext } from "../eventState";

export type EventRsvpItem = {
  id: string;
  firstName: string;
  lastName: string | null;
  profileImageUrl: string | null;
  createdAt: string;
};

type EventCache = ApiResponse<PublicEventResponse>;


export function useEventRsvps(slug: string | null) {
  return useQuery({
    queryKey: ["eventRsvps", slug],
    queryFn: async () =>
      api.get<EventRsvpItem[]>(`/events/public/${slug}/rsvps`),
    enabled: !!slug,
  });
}

export function useEventInterested(slug: string | null) {
  return useQuery({
    queryKey: ["eventInterested", slug],
    queryFn: async () =>
      api.get<EventRsvpItem[]>(`/events/public/${slug}/interested`),
    enabled: !!slug,
  });
}

export function useRsvpEvent(slug: string) {
  const qc = useQueryClient();
  const es = useEventStateContext();
  return useMutation({
    mutationFn: async () =>
      api.post<{ id?: string }>(`/events/${slug}/rsvp`, {}),
    onMutate: () => {
      es.setRsvped(slug, true);
      const prev = qc.getQueryData<EventCache>(["eventPublic", slug]);
      qc.setQueryData<EventCache>(["eventPublic", slug], (old) => {
        if (!old?.data) return old;
        const count = (old.data.attendeeCount ?? 0) + 1;
        return { ...old, data: { ...old.data, isRsvped: true, attendeeCount: count } };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      es.setRsvped(slug, false);
      if (ctx?.prev) qc.setQueryData(["eventPublic", slug], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["eventPublic", slug] });
      qc.invalidateQueries({ queryKey: ["eventRsvps", slug] });
    },
  });
}

export function useCancelRsvp(slug: string) {
  const qc = useQueryClient();
  const es = useEventStateContext();
  return useMutation({
    mutationFn: async () => api.delete<unknown>(`/events/${slug}/rsvp`),
    onMutate: () => {
      const prev = qc.getQueryData<EventCache>(["eventPublic", slug]);
      qc.setQueryData<EventCache>(["eventPublic", slug], (old) => {
        if (!old?.data) return old;
        const count = Math.max(0, (old.data.attendeeCount ?? 1) - 1);
        return { ...old, data: { ...old.data, isRsvped: false, attendeeCount: count } };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["eventPublic", slug], ctx.prev);
    },
    onSettled: (_data, error) => {
      if (!error) es.setRsvped(slug, false);
      qc.invalidateQueries({ queryKey: ["eventPublic", slug] });
      qc.invalidateQueries({ queryKey: ["eventRsvps", slug] });
    },
  });
}

export function useMarkInterested(slug: string) {
  const qc = useQueryClient();
  const es = useEventStateContext();
  return useMutation({
    mutationFn: async () =>
      api.post<{ id?: string }>(`/events/${slug}/interested`, {}),
    onMutate: () => {
      es.setInterested(slug, true);
      const prev = qc.getQueryData<EventCache>(["eventPublic", slug]);
      qc.setQueryData<EventCache>(["eventPublic", slug], (old) => {
        if (!old?.data) return old;
        return { ...old, data: { ...old.data, isInterested: true, interestedCount: (old.data.interestedCount ?? 0) + 1 } };
      });
      // Optimistically add self to the interested list so the sheet shows the
      // user's face immediately without a separate fetch or flash of "no one yet".
      const prevInterested = qc.getQueryData(["eventInterested", slug]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const me = (qc.getQueryData<any>(["profile"]))?.data?.user;
      if (me?.id) {
        qc.setQueryData<ApiResponse<EventRsvpItem[]>>(["eventInterested", slug], (old) => {
          const list = old?.success ? (old.data ?? []) : [];
          if (list.some((r) => r.id === me.id)) return old;
          return {
            success: true,
            data: [...list, {
              id: me.id,
              firstName: me.firstName ?? "",
              lastName: me.lastName ?? null,
              profileImageUrl: me.profileImageUrl ?? null,
              createdAt: new Date().toISOString(),
            }],
          };
        });
      }
      return { prev, prevInterested };
    },
    onError: (_err, _vars, ctx) => {
      es.setInterested(slug, false);
      if (ctx?.prev) qc.setQueryData(["eventPublic", slug], ctx.prev);
      if (ctx?.prevInterested !== undefined) qc.setQueryData(["eventInterested", slug], ctx.prevInterested);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["eventPublic", slug] });
      qc.invalidateQueries({ queryKey: ["eventInterested", slug] });
    },
  });
}

export function useUnmarkInterested(slug: string) {
  const qc = useQueryClient();
  const es = useEventStateContext();
  return useMutation({
    mutationFn: async () => api.delete<unknown>(`/events/${slug}/interested`),
    onMutate: () => {
      es.setInterested(slug, false);
      const prev = qc.getQueryData<EventCache>(["eventPublic", slug]);
      qc.setQueryData<EventCache>(["eventPublic", slug], (old) => {
        if (!old?.data) return old;
        return { ...old, data: { ...old.data, isInterested: false, interestedCount: Math.max(0, (old.data.interestedCount ?? 1) - 1) } };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      es.setInterested(slug, true);
      if (ctx?.prev) qc.setQueryData(["eventPublic", slug], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["eventPublic", slug] });
      qc.invalidateQueries({ queryKey: ["eventInterested", slug] });
    },
  });
}
